import React, {useEffect, useState} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import {useLocation} from 'react-router-dom';
import {useTranslation} from "react-i18next";
import {RoutePaths} from "../../config/routeConstants";
import BackButton from "../../components/common/BackButton";
import {t} from "i18next";
import {Wrapper, TopBar} from './Manual.styled';

const Manual: React.FC = () => {
    const [content, setContent] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const location = useLocation();
    const {i18n} = useTranslation();
    const manualFile = i18n.language === 'pl' ? '/docs/manual_pl.md' : '/docs/manual_en.md';

    const baseUrl = React.useMemo(() => manualFile.replace(/[^/]+$/, ''), []);

    useEffect(() => {
        setIsLoading(true);
        fetch(manualFile)
            .then((r) => {
                return r.text();
            })
            .then((txt) => setContent(txt))
            .catch(() => setError(t('manual.message.error')))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        if (!location.hash) return;
        const id = decodeURIComponent(location.hash.slice(1));
        const el = document.getElementById(id);
        if (el) setTimeout(() => el.scrollIntoView({behavior: 'smooth', block: 'start'}), 100);
    }, [location.hash, content]);

    if (error) {
        return (
            <Wrapper>
                <TopBar>
                    <BackButton to={RoutePaths.Root} text={t('manual.returnButton') as string}/>
                </TopBar>
                {error}
            </Wrapper>
        );
    }

    return (
        <Wrapper role="region" aria-labelledby="manual-title">
            <TopBar>
                <BackButton to={RoutePaths.Root} text={t('manual.returnButton') as string}/>
            </TopBar>

            <h2 id="manual-title" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                {t('manual.region.ariaLabel')}
            </h2>

            <div aria-live="polite" aria-atomic={true} aria-busy={isLoading} aria-label={t('manual.content.ariaLabel') as string}>
                {isLoading && (
                    <div role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(1px, 1px, 1px, 1px)' }}>
                        {t('manual.loading')}
                    </div>
                )}

            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[
                    rehypeSlug,
                    [rehypeAutolinkHeadings, {behavior: 'wrap'}],
                ]}
                components={{
                    img({src, alt}) {
                        const resolved = src ? new URL(src, window.location.origin + baseUrl).toString() : undefined;
                        return (
                            <img
                                src={resolved}
                                alt={alt as string}
                                style={{
                                    maxWidth: '100%',
                                    display: 'block',
                                    margin: '20px auto',
                                    borderRadius: 4,
                                }}
                            />
                        );
                    },
                }}
            >
                {content}
            </ReactMarkdown>
            </div>
        </Wrapper>
    );
};

export default Manual;
